/**
 * Shared JS translation helper.
 *
 * None of the built entries (designer-app, admin-app, product-designer,
 * order-design, woo-panel, design-download) are guaranteed to be registered
 * via wp_register_script/wp_enqueue_script (admin-app in particular is
 * injected as a raw <script type="module"> tag), so `wp_set_script_translations()`
 * — which requires a registered handle plus generated per-locale JSON files —
 * cannot be relied on everywhere. Instead the PHP side prints a small inline
 * `window.veyopL10n` map (current-locale strings, translated server-side via
 * `__()`) alongside each app's config script tag; this helper reads that map
 * first and only falls back to `wp.i18n` / the raw string.
 */

declare global {
  interface Window {
    wp?: {
      apiFetch?: <T>(options: { path: string }) => Promise<T>;
      i18n?: {
        __?: (text: string, domain?: string) => string;
      };
    };
    veyopL10n?: Record<string, string>;
    veyopL10nDomains?: Record<string, Record<string, string>>;
    veyopLocale?: string;
    veyopIsRtl?: boolean;
  }
}

const DOMAIN = 'veyo-product-personalizer';

/** Translate a string for the current request locale. */
export function t(text: string, domain: string = DOMAIN): string {
  if (typeof window === 'undefined') return text;
  const domainInjected = window.veyopL10nDomains?.[domain]?.[text];
  if (typeof domainInjected === 'string' && domainInjected !== '') {
    return domainInjected;
  }
  if (domain === DOMAIN) {
    const injected = window.veyopL10n?.[text];
    if (typeof injected === 'string' && injected !== '') {
      return injected;
    }
  }
  const viaWpI18n = window.wp?.i18n?.__?.(text, domain);
  if (typeof viaWpI18n === 'string' && viaWpI18n !== '') {
    return viaWpI18n;
  }
  return text;
}

/** Convenience alias matching the local `t()` helpers previously duplicated per view. */
export const __ = t;

const TRANSLATABLE_ATTRIBUTES = ['aria-label', 'placeholder', 'title'] as const;

/** Apply active locale metadata to a Veyo application root. */
export function applyLocaleMetadata(root: HTMLElement): void {
  root.lang = (window.veyopLocale || document.documentElement.lang || 'en').replace(/_/g, '-');
  root.dir = window.veyopIsRtl ? 'rtl' : 'ltr';
  root.classList.toggle('veyop-rtl', Boolean(window.veyopIsRtl));
}

/**
 * Translate legacy literal DOM text while components are migrated to t().
 * Dynamic/customer content can opt out with data-veyop-no-translate.
 */
export function translateDom(root: HTMLElement): () => void {
  applyLocaleMetadata(root);

  const translateNode = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.nodeValue || '';
      const value = raw.trim();
      if (!value) return;
      const translated = t(value);
      if (translated !== value) {
        node.nodeValue = raw.replace(value, translated);
      }
      return;
    }
    if (!(node instanceof Element)) return;
    if (node.matches('script, style, code, pre, [data-veyop-no-translate]')) return;
    for (const attribute of TRANSLATABLE_ATTRIBUTES) {
      const value = node.getAttribute(attribute);
      if (!value) continue;
      const translated = t(value);
      if (translated !== value) node.setAttribute(attribute, translated);
    }
    node.childNodes.forEach(translateNode);
  };

  translateNode(root);
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'characterData') translateNode(record.target);
      record.addedNodes.forEach(translateNode);
      if (record.type === 'attributes') translateNode(record.target);
    }
  });
  observer.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...TRANSLATABLE_ATTRIBUTES],
  });

  // Vue Teleport content lives outside the app root. Observe only explicitly
  // marked Veyo overlays, never unrelated WordPress/theme DOM.
  const teleportObserver = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'characterData') {
        if (record.target.parentElement?.closest('[data-veyop-i18n-root]')) translateNode(record.target);
        continue;
      }
      if (record.type === 'attributes') {
        if ((record.target as Element).closest?.('[data-veyop-i18n-root]')) translateNode(record.target);
        continue;
      }
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('[data-veyop-i18n-root]') || node.closest('[data-veyop-i18n-root]')) {
          translateNode(node);
          return;
        }
        node.querySelectorAll('[data-veyop-i18n-root]').forEach(translateNode);
      });
    }
  });
  teleportObserver.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...TRANSLATABLE_ATTRIBUTES],
  });
  document.querySelectorAll('[data-veyop-i18n-root]').forEach(translateNode);

  return () => {
    observer.disconnect();
    teleportObserver.disconnect();
  };
}
